import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Link2 } from 'lucide-react';
import { parseRecipeFromURL } from '../utils/recipeParser';
import { 
  validateUrl, 
  processParsedRecipe, 
  SUPPORTED_SITES,
  getImportErrorMessage 
} from './ImportFromURLPage.helpers';
import {
  SupportedSites,
  TipsSection,
  DebugInfo,
  ErrorMessage
} from './ImportFromURLPage.components';

const ImportFromURLPage: React.FC = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string[]>([]);
  const [showDebug, setShowDebug] = useState(false);

  const addDebugInfo = (message: string) => {
    setDebugInfo(prev => [...prev, `${new Date().toISOString().split('T')[1].split('.')[0]} - ${message}`]);
  };

  const handleImport = async () => {
    const validationError = validateUrl(url);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError(null);
    setDebugInfo([]);
    addDebugInfo('Starting import process');

    try {
      // Add overall timeout for the entire import process (30 seconds)
      const importTimeout = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Import timeout after 30 seconds')), 30000);
      });

      addDebugInfo(`Parsing recipe from URL: ${url}`);
      
      // Race between parsing and timeout
      const parsedRecipe = await Promise.race([
        parseRecipeFromURL(url),
        importTimeout
      ]);
      
      addDebugInfo('Recipe parsed successfully');
      
      // Process the parsed recipe
      const newRecipe = await processParsedRecipe(parsedRecipe, addDebugInfo);
      
      addDebugInfo('Redirecting to recipe page');
      navigate(`/app/recipes/${newRecipe.id}`);
    } catch (err) {
      const error = err as Error;
      addDebugInfo(`Import failed: ${error.message}`);
      setError(getImportErrorMessage(error));
      setShowDebug(true); // Show debug info on error
    } finally {
      setIsLoading(false);
      addDebugInfo('Import process completed');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleImport();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/app/recipes')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-semibold">Import Recipe from URL</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* URL Input Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Link2 className="text-[#4A6651]" size={24} />
            <h2 className="text-lg font-semibold">Enter Recipe URL</h2>
          </div>
          
          <div className="space-y-4">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Paste recipe URL here (e.g., https://www.example.com/recipe)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4A6651] text-base"
              disabled={isLoading}
            />
            
            {error && <ErrorMessage error={error} />}
            
            <button
              onClick={handleImport}
              disabled={isLoading || !url.trim()}
              className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                isLoading || !url.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-[#4A6651] text-white hover:bg-[#3d5444]'
              }`}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Importing recipe...
                </span>
              ) : (
                'Import Recipe'
              )}
            </button>
          </div>
        </div>

        {/* Debug Info (shown on error) */}
        {showDebug && debugInfo.length > 0 && (
          <DebugInfo debugInfo={debugInfo} onHide={() => setShowDebug(false)} />
        )}

        {/* Supported Sites */}
        <SupportedSites sites={SUPPORTED_SITES} />

        {/* Tips Section */}
        <TipsSection />
      </div>
    </div>
  );
};

export default ImportFromURLPage;